import { RESULT_DISPLAY_SIZE, RESULT_WRITING_V2_SIZE } from '@/constant';
import {
    Question,
    Stroke,
    Stroke2Path,
    WritingStrokes,
    isSlideComponentCharacter,
    isSlideComponentCharacterWriting,
    isSlideComponentRadicalWriting,
    isSlideComponentStrokeWriting,
    isWritingResult,
} from '@/types/slide';
import { Box, Card, CardActionArea, CardContent } from '@mui/material';
import Konva from 'konva';
import { ShapeConfig } from 'konva/lib/Shape';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Layer, Path, Stage } from 'react-konva';

type AnimatedStroke = {
    coordinates: Stroke;
    persist?: boolean;
    color?: ShapeConfig['stroke'];
};

type BackgroundModel = {
    character: string;
    code: string;
};

function CharacterAnswer2BackgroundModel(question: Question): BackgroundModel | undefined {
    if (isSlideComponentCharacter(question)) {
        const character = question.content.characterList[question.content.character];
        if (character && character.code && character.character) {
            return { character: character.character, code: character.code };
        }
    }

    return undefined;
}

function CharacterWritingAnswer2BackgroundModel(question: Question): BackgroundModel | undefined {
    if (isSlideComponentCharacterWriting(question)) {
        if (question.content.character && question.content.code) {
            return { character: question.content.character, code: question.content.code };
        }
    }
    if (isSlideComponentStrokeWriting(question)) {
        if (question.content.character && question.content.code) {
            return { character: question.content.character, code: question.content.code };
        }
    }
    if (isSlideComponentRadicalWriting(question)) {
        if (question.content.character && question.content.code) {
            return { character: question.content.character, code: question.content.code };
        }
    }

    return undefined;
}

interface QuestionProps {
    type: 'question';
    viewOnly: boolean;
    question: Question;
    showReference?: boolean;
}

interface WritingStrokesProps {
    type: 'writingStrokes';
    viewOnly: boolean;
    writingStrokes: WritingStrokes;
}

type ResultDisplayWritingProps = QuestionProps | WritingStrokesProps;

export function ResultDisplayWriting(props: ResultDisplayWritingProps) {
    // display values

    let strokes: AnimatedStroke[] = [];
    let background = 'none';
    let borderRadius = 1;

    if (props.type === 'question') {
        const answer = props.question.answers?.[0];
        const content = answer?.data ? JSON.parse(answer.data) : undefined;
        const result = content && isWritingResult(content) ? content : undefined;
        strokes =
            result?.writing.normalMode === true
                ? result?.writing.strokes.reduce((strokes, history) => {
                      history.forEach((coordinates, index) => {
                          if (index === history.length - 1) {
                              strokes.push({ coordinates, persist: true });
                          } else {
                              strokes.push({ coordinates, persist: false, color: 'red' });
                          }
                      });
                      return strokes;
                  }, [] as AnimatedStroke[])
                : (result?.writing.strokes.map((coordinates) => ({ coordinates })) ?? []);
        const backgroundModel = CharacterAnswer2BackgroundModel(props.question) ?? CharacterWritingAnswer2BackgroundModel(props.question);

        if (props.showReference === false) {
            background = 'none';
        } else if (props.question.type === 'strokeWriting') {
            background = `url(/asset/strokes/svg/${backgroundModel?.code}_${backgroundModel?.character}/${backgroundModel?.character}.svg)`;
        } else if (props.question.type === 'radicalWriting') {
            background = `url(/asset/radicalDeformations/svg/${backgroundModel?.code}_${backgroundModel?.character}/${backgroundModel?.character}.svg)`;
        } else {
            background = `url(/asset/svg/${backgroundModel?.code}_${backgroundModel?.character}/${backgroundModel?.character}.svg)`;
        }
    } else if (props.type === 'writingStrokes') {
        borderRadius = 0;
        strokes =
            props.writingStrokes.normalMode === true
                ? props.writingStrokes.strokes.reduce((strokes, history) => {
                      history.forEach((coordinates, index) => {
                          if (index === history.length - 1) {
                              strokes.push({ coordinates, persist: true });
                          } else {
                              strokes.push({ coordinates, persist: false, color: 'red' });
                          }
                      });
                      return strokes;
                  }, [] as AnimatedStroke[])
                : (props.writingStrokes.strokes.map((coordinates) => ({ coordinates })) ?? []);
    }

    // ui state
    const strokesRef = useRef<{ stroke: AnimatedStroke; path: Konva.Path; tween?: Konva.Tween }[]>([]);
    const mainRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ width: RESULT_DISPLAY_SIZE.width, height: RESULT_DISPLAY_SIZE.height });

    useEffect(() => {
        if (mainRef.current) {
            setSize({
                width: mainRef.current.offsetWidth ?? mainRef.current.getBoundingClientRect().width,
                height: mainRef.current.offsetHeight ?? mainRef.current.getBoundingClientRect().height,
            });
        }
    }, []);

    const replay = useCallback(async () => {
        // reset all stroke and setup new tweens
        strokesRef.current.forEach((ref) => {
            // clear all paths
            const pathLen = ref.path.getLength();
            ref.path.dashOffset(pathLen);
            ref.path.dash([pathLen]);
            ref.path.visible(false);

            // stop all previous tweens before playing the new one
            if (ref.tween) {
                ref.tween.reset();
            }

            // create a new tween for each path
            ref.tween = new Konva.Tween({
                node: ref.path,
                dashOffset: 0,
                duration: 0.5,
            });
        });

        // play all new tweens in sequence
        for (const stroke of strokesRef.current) {
            const animation = new Promise<void>((resolve, reject) => {
                if (stroke.tween) {
                    stroke.tween.onReset = () => reject();
                    stroke.tween.onFinish = () => resolve();
                    /**
                     * +1 is to workaround if using round lineCap,
                     * some path would have crop a bit of the offset'ed dash hence this workaround
                     *
                     * can remove the +1 length and the visible toggle if not using round lineCap
                     */
                    stroke.path.dash([stroke.path.getLength() + 1]);
                    // hide it and reval it only before animation is the same reason as above
                    stroke.path.visible(true);
                    stroke.tween.play(); // execute the tween
                } else {
                    resolve();
                }
            });

            try {
                await animation;
            } catch {
                // we might cancel the animation e.g. user click replay again
                break;
            } finally {
                // clear it only if persiste is set explicitly to false
                if (stroke.stroke.persist === false) {
                    stroke.path.visible(false);
                }
            }
        }
    }, []);

    return (
        <Card
            data-mui-color-scheme='light'
            sx={{
                width: '100%',
                height: '100%',
                p: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: borderRadius,
                position: 'relative',
                backgroundColor: 'white !important', //not sure why but need !important for Cards
            }}
            data-testid='result-display-writing'
            ref={mainRef}
        >
            <CardActionArea
                sx={{
                    cursor: !props.viewOnly ? 'pointer' : 'default',
                }}
            >
                <CardContent>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            background: background,
                            backgroundSize: 'cover',
                            opacity: 0.15,
                        }}
                    />
                    <Stage
                        width={size.width}
                        height={size.height}
                        onClick={async () => {
                            if (!props.viewOnly) {
                                await replay();
                            }
                        }}
                    >
                        <Layer>
                            {strokes?.map((stroke, index) => {
                                const data = Stroke2Path(stroke.coordinates, size.width / RESULT_WRITING_V2_SIZE.width);
                                return (
                                    <Path
                                        key={index}
                                        x={0}
                                        y={0}
                                        stroke={stroke.color ?? '#000000'}
                                        visible={stroke.persist !== false}
                                        lineCap='round'
                                        strokeWidth={6}
                                        data={data}
                                        ref={(el) => {
                                            if (el) {
                                                strokesRef.current[index] = { stroke: stroke, path: el };
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Layer>
                    </Stage>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}
